import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SeverityBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Check, Send, RefreshCw, ShieldCheck } from "lucide-react";

const VERDICT = {
  valid: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  review: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  incorrect: "text-red-400 border-red-500/40 bg-red-500/10",
};

function Stat({ label, value, accent }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 p-3">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`font-mono text-xl mt-0.5 ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

export default function HealthCheckDetail() {
  const { hcId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});

  const { data: hc, isLoading } = useQuery({
    queryKey: ["healthcheck", hcId],
    queryFn: async () => (await api.get(`/health-checks/${hcId}`)).data,
    refetchInterval: (q) => (q.state.data?.status === "analyzing" ? 3000 : false),
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["healthcheck", hcId] }); qc.invalidateQueries({ queryKey: ["healthchecks"] }); };

  const rerun = useMutation({ mutationFn: async () => (await api.post(`/health-checks/${hcId}/run`)).data,
    onSuccess: () => { toast.success("Re-analyzed"); invalidate(); }, onError: () => toast.error("Failed") });
  const reviewLine = useMutation({
    mutationFn: async ({ lineId, body }) => (await api.post(`/health-checks/${hcId}/lines/${lineId}/review`, body)).data,
    onSuccess: () => { toast.success("Line reviewed"); invalidate(); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const approve = useMutation({ mutationFn: async () => (await api.post(`/health-checks/${hcId}/approve`)).data,
    onSuccess: () => { toast.success("Approved — report generated"); invalidate(); }, onError: () => toast.error("Failed") });
  const send = useMutation({ mutationFn: async () => (await api.post(`/health-checks/${hcId}/send`)).data,
    onSuccess: (d) => { d.sent ? toast.success("Report sent to client") : toast.error(`Send failed: ${d.error}`); invalidate(); }, onError: () => toast.error("Failed") });

  if (isLoading) return <div className="p-10 text-center text-zinc-500"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!hc) return <div className="p-10 text-zinc-500">Not found.</div>;
  const s = hc.summary || {};

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3 sticky top-0 bg-background z-10">
        <button data-testid="hc-back" onClick={() => navigate("/health-checks")} className="p-1.5 border border-zinc-800 rounded-sm hover:bg-zinc-900 text-zinc-400"><ArrowLeft className="h-4 w-4" /></button>
        <h1 className="font-heading text-base font-semibold font-mono text-blue-400">{hc.hc_id}</h1>
        <span className="text-xs text-zinc-400">{hc.client_name}</span>
        <span className="text-xs font-mono text-zinc-600">{hc.client_email}</span>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="hc-rerun" onClick={() => rerun.mutate()} disabled={rerun.isPending}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
            {rerun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Re-analyze
          </button>
          <button data-testid="hc-approve" onClick={() => approve.mutate()} disabled={approve.isPending}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-emerald-700 text-emerald-400 rounded-sm hover:bg-emerald-900/30 disabled:opacity-50">
            {approve.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Approve report
          </button>
          <button data-testid="hc-send" onClick={() => send.mutate()} disabled={send.isPending || hc.status === "created"}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-sm disabled:opacity-50">
            {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send to client
          </button>
        </div>
      </header>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total lines" value={s.total ?? 0} />
          <Stat label="Valid" value={s.valid ?? 0} accent="text-emerald-400" />
          <Stat label="To review" value={s.review ?? 0} accent="text-amber-400" />
          <Stat label="Incorrect" value={s.incorrect ?? 0} accent="text-red-400" />
          <Stat label="High risk" value={s.high_risk ?? 0} accent={s.high_risk ? "text-red-400" : "text-white"} />
        </div>

        <div className="border border-zinc-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">Product</th>
                <th className="text-left font-medium py-2 px-3">Declared</th>
                <th className="text-left font-medium py-2 px-3">Recommended</th>
                <th className="text-left font-medium py-2 px-3">Verdict</th>
                <th className="text-left font-medium py-2 px-3">Risk</th>
                <th className="text-left font-medium py-2 px-3">Duty Δ</th>
                <th className="text-left font-medium py-2 px-3">Assessment</th>
                <th className="text-left font-medium py-2 px-3">Review</th>
              </tr>
            </thead>
            <tbody>
              {(hc.lines || []).map((ln) => {
                const changed = ln.recommended_hs_code && ln.recommended_hs_code !== ln.declared_hs_code;
                return (
                  <tr key={ln.line_id} data-testid={`hc-line-${ln.line_id}`} className="border-t border-zinc-800/60 align-top hover:bg-zinc-900/50">
                    <td className="py-2 px-3 text-zinc-300 max-w-[220px]">{ln.description}</td>
                    <td className="py-2 px-3 font-mono text-zinc-400">{ln.declared_hs_code || "—"}</td>
                    <td className={`py-2 px-3 font-mono ${changed ? "text-blue-400" : "text-zinc-300"}`}>{ln.recommended_hs_code || "—"}</td>
                    <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono ${VERDICT[ln.verdict] || "border-zinc-700 text-zinc-400"}`}>{(ln.verdict || "—").toUpperCase()}</span></td>
                    <td className="py-2 px-3"><SeverityBadge severity={ln.risk}>{(ln.risk || "—").toUpperCase()}</SeverityBadge></td>
                    <td className="py-2 px-3 font-mono text-xs">{ln.duty_delta == null ? "—" : <span className={ln.duty_delta > 0 ? "text-red-400" : ln.duty_delta < 0 ? "text-emerald-400" : "text-zinc-400"}>{ln.duty_delta > 0 ? "+" : ""}{ln.duty_delta}%</span>}</td>
                    <td className="py-2 px-3 text-[11px] text-zinc-400 max-w-[240px]">{ln.reasoning}{ln.reviewed && <span className="ml-1 text-emerald-500">✓</span>}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <input data-testid={`hc-line-input-${ln.line_id}`} placeholder={ln.recommended_hs_code}
                          value={edits[ln.line_id] ?? ""} onChange={(e) => setEdits({ ...edits, [ln.line_id]: e.target.value })}
                          className="w-24 bg-zinc-950 border border-zinc-800 rounded-sm px-1.5 py-1 text-[11px] font-mono focus:border-blue-500 outline-none" />
                        <button data-testid={`hc-line-review-${ln.line_id}`}
                          onClick={() => reviewLine.mutate({ lineId: ln.line_id, body: { recommended_hs_code: edits[ln.line_id] || ln.recommended_hs_code, reviewed: true } })}
                          className="p-1 border border-emerald-700 text-emerald-400 rounded-sm hover:bg-emerald-900/30"><Check className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hc.report_html && (
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/40">
            <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-400">Client report preview</div>
            <div className="p-4 bg-white text-black text-xs overflow-auto max-h-96" dangerouslySetInnerHTML={{ __html: hc.report_html }} />
          </div>
        )}
      </div>
    </div>
  );
}
